import { useEffect, useRef, useState } from 'react';
import { useI18n } from '@/ui/i18n/I18nProvider';
import { shopThemes } from '@/core/domain/shop';
import { useSave, useServices } from '@/ui/services/ServicesContext';
import styles from './ShopBarks.module.css';

const SHOW_MS = 2600;
/** Not every sale talks: a quiet shop feels calmer (plan §2 "êm dịu"). */
const BUY_BARK_CHANCE = 0.45;
const POOLS = { enter: 6, buy: 7, nostock: 5 } as const;
/** story-dialogue §10: a decor remark at most once every 3 customers, only for themes the Tiệm shows. */
const THEME_EVERY = 3;

/**
 * One-line customer barks in the Tiệm (story-dialogue §9). Presentation only, so `Math.random` is fine here:
 * the domain decides who comes and what they buy; this just picks which friendly line to show.
 */
export function ShopBarks() {
  const { t } = useI18n();
  const { bus, content, store } = useServices();
  const arrivals = useRef(0);
  const inShop = useSave((s) => s.lastSession.sceneId === 'minimart');
  const [bark, setBark] = useState<{ id: number; key: string; caption?: string; anywhere?: boolean } | null>(
    null,
  );
  const seq = useRef(0);
  const inShopRef = useRef(inShop);
  useEffect(() => {
    inShopRef.current = inShop;
  });

  useEffect(
    () =>
      bus.onAny((e) => {
        // Sếp Mèo answers wherever she was petted (the Tiệm, or Home when she visits, M3-08).
        if (e.type === 'CAT_PETTED' && e.dialogueId) {
          const n = e.dialogueId.slice(e.dialogueId.lastIndexOf('_') + 1);
          setBark({
            id: ++seq.current,
            key: `cat.tap.${n}`,
            caption: `cat.tap_caption.${n}`,
            anywhere: true,
          });
          return;
        }
        if (!inShopRef.current) return;
        const pick = (pool: keyof typeof POOLS) =>
          `customer.bark.${pool}.${String(1 + Math.floor(Math.random() * POOLS[pool]))}`;
        let key: string | null = null;
        if (e.type === 'CUSTOMER_ARRIVED') {
          arrivals.current += 1;
          const themes = [...shopThemes(content, store.getState())];
          if (arrivals.current % THEME_EVERY === 0 && themes.length > 0) {
            const tag = themes[Math.floor(Math.random() * themes.length)];
            key =
              Math.random() < 0.3
                ? `customer.bark.theme.${String(1 + Math.floor(Math.random() * 2))}`
                : `customer.bark.theme_${String(tag)}.1`;
          } else key = pick('enter');
        } else if (e.type === 'CUSTOMER_LEFT_EMPTY') key = pick('nostock');
        else if (e.type === 'SALE_COMPLETED' && Math.random() < BUY_BARK_CHANCE) key = pick('buy');
        if (key) setBark({ id: ++seq.current, key });
      }),
    [bus, content, store],
  );

  useEffect(() => {
    if (!bark) return;
    const timer = setTimeout(() => {
      setBark(null);
    }, SHOW_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [bark]);

  if (!bark || (!inShop && !bark.anywhere)) return null;
  return (
    <div className={styles.host} aria-live="polite">
      <div
        key={bark.id}
        className={styles.bubble}
        data-testid="shop-bark"
        data-cat={bark.caption ? true : undefined}
      >
        {t(bark.key)}
        {bark.caption && <small className={styles.caption}>{t(bark.caption)}</small>}
      </div>
    </div>
  );
}
